/**
 * Users 相關的 SWR Hooks
 */

import useSWR from 'swr';
import {
  useUsersGetByLineId,
  useUsersGetPointsHistory,
  useGetApiCustomersId,
  type UsersGetPointsHistoryParams,
  type GetApiCustomersId200Data,
} from '@pos/api-sdk';
import { getApiHeaders, getTenantIdFromUrl } from '../../api/apiUtils';
import { getOrCreateByLineId } from '../../api/memberClient';
import { useTenant } from '../../contexts/TenantContext';
import { swrDetailConfig, swrListConfig } from '../../lib/swr-config';
import type { SWRConfiguration } from 'swr';

/**
 * 登入即加會員：依 LINE ID 取得或建立會員，保證回傳 id（用於 member / preorder / survey）
 */
export function useEnsureMemberByLineId(
  lineId: string | undefined,
  displayName?: string,
  options?: { enabled?: boolean }
) {
  const { tenantId } = useTenant();
  const urlTenantId = getTenantIdFromUrl();
  const t = tenantId || urlTenantId || undefined;
  const enabled = options?.enabled !== false;

  const { data, error, isLoading, mutate } = useSWR(
    enabled && lineId && t
      ? ['ensure-member', lineId, displayName ?? '', t]
      : null,
    () => getOrCreateByLineId(lineId!, displayName, t),
    { revalidateOnFocus: false, dedupingInterval: 10000 }
  );

  return {
    data: data?.data
      ? {
          ...data.data,
          id: String((data.data as any).id),
        }
      : undefined,
    isLoading,
    error,
    mutate,
  };
}

/**
 * 根據 LINE ID 取得使用者資訊
 */
export function useUserByLineId(
  lineId: string | undefined,
  options?: {
    fetch?: RequestInit;
    revalidateOnFocus?: boolean;
    dedupingInterval?: number;
    enabled?: boolean;
    swr?: SWRConfiguration;
  }
) {
  // 優先使用 TenantContext 的 tenantId，如果沒有則從 URL 取得
  const { tenantId: contextTenantId } = useTenant();
  const urlTenantId = getTenantIdFromUrl();
  const tenantId = contextTenantId || urlTenantId;

  const {
    revalidateOnFocus = false,
    dedupingInterval = 5000,
    enabled = true,
    swr: swrOptions,
  } = options || {};

  // 取得 headers 並確保正確傳遞
  const apiHeaders = getApiHeaders(tenantId);

  const result = useUsersGetByLineId(lineId ?? '', {
    fetch: {
      ...options?.fetch,
      headers: {
        ...apiHeaders,
        ...(options?.fetch?.headers || {}),
      },
    },
    swr: {
      ...swrDetailConfig,
      enabled: enabled && !!lineId && !!tenantId,
      revalidateOnFocus,
      dedupingInterval,
      ...swrOptions,
    },
  });

  // 提取實際資料（by-line-id 回傳 { data: 顧客物件 }，顧客物件即為 user）
  const rawData = result.data?.data;
  const userData = rawData as unknown as GetApiCustomersId200Data | undefined;
  const user: GetApiCustomersId200Data | undefined =
    userData && typeof userData === 'object' && 'data' in userData
      ? (userData as { data: GetApiCustomersId200Data }).data
      : (userData ?? undefined);

  return {
    user,
    isLoading: result.isLoading,
    isError: !!result.error,
    error: result.error,
    mutate: result.mutate,
    isValidating: result.isValidating,
  };
}

/**
 * 取得使用者點數交易歷史
 */
export function usePointsHistory(
  userId: string | undefined,
  params?: UsersGetPointsHistoryParams,
  options?: {
    fetch?: RequestInit;
    revalidateOnFocus?: boolean;
    dedupingInterval?: number;
    enabled?: boolean;
    swr?: SWRConfiguration;
  }
) {
  // 優先使用 TenantContext 的 tenantId，如果沒有則從 URL 取得
  const { tenantId: contextTenantId } = useTenant();
  const urlTenantId = getTenantIdFromUrl();
  const tenantId = contextTenantId || urlTenantId;

  const {
    revalidateOnFocus = false,
    dedupingInterval = 1000,
    enabled = true,
    swr: swrOptions,
  } = options || {};

  // 取得 headers 並確保正確傳遞
  const apiHeaders = getApiHeaders(tenantId);

  const result = useUsersGetPointsHistory(userId ?? '', params, {
    fetch: {
      ...options?.fetch,
      headers: {
        ...apiHeaders,
        ...(options?.fetch?.headers || {}),
      },
    },
    swr: {
      ...swrListConfig,
      enabled: enabled && !!userId && !!tenantId,
      revalidateOnFocus,
      dedupingInterval,
      ...swrOptions,
    },
  });

  // 提取實際資料
  const pointsData = result.data?.data;
  const points =
    pointsData && 'data' in pointsData && Array.isArray(pointsData.data)
      ? pointsData.data
      : undefined;

  return {
    points,
    isLoading: result.isLoading,
    isError: !!result.error,
    error: result.error,
    mutate: result.mutate,
    isValidating: result.isValidating,
  };
}

/**
 * 取得使用者詳細資訊
 */
export function useUserDetail(
  userId: string | undefined,
  options?: {
    fetch?: RequestInit;
    revalidateOnFocus?: boolean;
    dedupingInterval?: number;
    enabled?: boolean;
    swr?: SWRConfiguration;
  }
) {
  // 優先使用 TenantContext 的 tenantId，如果沒有則從 URL 取得
  const { tenantId: contextTenantId } = useTenant();
  const urlTenantId = getTenantIdFromUrl();
  const tenantId = contextTenantId || urlTenantId;

  const {
    revalidateOnFocus = false,
    dedupingInterval = 5000,
    enabled = true,
    swr: swrOptions,
  } = options || {};

  // 取得 headers 並確保正確傳遞
  const apiHeaders = getApiHeaders(tenantId);

  const result = useGetApiCustomersId(userId ?? '', {
    fetch: {
      ...options?.fetch,
      headers: {
        ...apiHeaders,
        ...(options?.fetch?.headers || {}),
      },
    },
    swr: {
      ...swrDetailConfig,
      enabled: enabled && !!userId && !!tenantId,
      revalidateOnFocus,
      dedupingInterval,
      ...swrOptions,
    },
  });

  // 提取實際資料：GET /api/customers/:id 回傳 { data: 顧客物件 }，顧客物件即為 user
  const rawData = result.data?.data;
  const userData = rawData as unknown as GetApiCustomersId200Data | undefined;
  const user: GetApiCustomersId200Data | undefined =
    userData && typeof userData === 'object' && 'data' in userData
      ? (userData as { data: GetApiCustomersId200Data }).data
      : (userData ?? undefined);

  return {
    user,
    isLoading: result.isLoading,
    isError: !!result.error,
    error: result.error,
    mutate: result.mutate,
    isValidating: result.isValidating,
  };
}
